import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import {
  Sparkles, ChevronLeft, Loader2, RotateCcw,
  Brain, ArmchairIcon, MoveDiagonal, Bed, Plane, Activity,
} from "lucide-react";

const GOALS = [
  { id: "neck",     label: "Neck Relief",     icon: Brain,        primaryGoal: "Pain relief",      biasArea: "Neck",       blurb: "Stiffness, screen strain, side-sleep pain." },
  { id: "back",     label: "Back Comfort",    icon: ArmchairIcon, primaryGoal: "Pain relief",      biasArea: "Lower back", blurb: "Long sitting, lifting, end-of-day ache." },
  { id: "posture",  label: "Posture Reset",   icon: MoveDiagonal, primaryGoal: "Better posture",   biasArea: "Upper back", blurb: "Realign desk-chair posture, head-to-hip." },
  { id: "sleep",    label: "Better Sleep",    icon: Bed,          primaryGoal: "Improved sleep",   biasArea: "Neck",       blurb: "Wake without stiffness or numb arms." },
  { id: "travel",   label: "Travel Comfort",  icon: Plane,        primaryGoal: "Travel comfort",   biasArea: "Travel",     blurb: "Flights, road trips, sleeping upright." },
  { id: "recovery", label: "Recovery",        icon: Activity,     primaryGoal: "Recovery support", biasArea: "Lower back", blurb: "Post-injury, postpartum, gentle support." },
];

const QUESTIONS = [
  {
    id: "area", type: "single", title: "Where does it bother you most?",
    options: [
      { value: "Neck",       label: "Neck",              hint: "Stiffness, side-sleep pain, screen strain" },
      { value: "Upper back", label: "Upper back",        hint: "Hunched shoulders, between blades" },
      { value: "Lower back", label: "Lower back",        hint: "Long sitting, lifting, end-of-day ache" },
      { value: "Tailbone",   label: "Tailbone",          hint: "Sharp pain when sitting, postpartum" },
      { value: "Travel",     label: "Travel discomfort", hint: "Flights, road trips, sleeping upright" },
    ],
  },
  {
    id: "when", type: "single", title: "When does it feel worst?",
    options: [
      { value: "Mornings after sleep",    label: "Right after I wake up" },
      { value: "While sitting at a desk", label: "While sitting at a desk" },
      { value: "While driving or flying", label: "While driving or flying" },
      { value: "By the end of the day",   label: "By the end of the day" },
      { value: "All the time",            label: "It's pretty much constant" },
    ],
  },
  {
    id: "duration", type: "single", title: "How long has it been going on?",
    options: [
      { value: "Just started this week",    label: "Just started this week" },
      { value: "A few weeks",               label: "A few weeks" },
      { value: "Several months",            label: "Several months" },
      { value: "Years — it comes and goes", label: "Years — comes and goes" },
    ],
  },
  {
    id: "routine", type: "single", title: "What does most of your day look like?",
    options: [
      { value: "Desk job — 8+ hours sitting",      label: "Desk job, 8+ hours sitting" },
      { value: "Frequent traveler",                 label: "Frequent traveler" },
      { value: "Active — on my feet a lot",         label: "Active, on my feet a lot" },
      { value: "Recovering from injury / surgery",  label: "Recovering from injury or surgery" },
      { value: "Mixed — a bit of everything",       label: "A bit of everything" },
    ],
  },
  {
    id: "goal", type: "multi", title: "What matters most to you?", subtitle: "Pick up to three — we'll weight your match around these.",
    options: [
      { value: "Pain relief",          label: "Pain relief" },
      { value: "Better posture",       label: "Better posture" },
      { value: "Improved sleep",       label: "Improved sleep" },
      { value: "Long sitting comfort", label: "Long sitting comfort" },
      { value: "Travel comfort",       label: "Travel comfort" },
      { value: "Recovery support",     label: "Recovery support" },
    ],
  },
];

export default function SymptomCheckerPage() {
  const navigate = useNavigate(); // eslint-disable-line no-unused-vars
  const [goal, setGoal] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const advanceTimer = useRef(null);

  useEffect(() => () => clearTimeout(advanceTimer.current), []);

  // Always start each step at the top of the viewport — no half-scrolled views
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step, goal]);

  const q = QUESTIONS[step];
  const current = answers[q?.id];

  const selectGoal = (g) => {
    setGoal(g);
    setAnswers({ goal: [g.primaryGoal], area: g.biasArea });
    setStep(0);
    setResult(null);
  };

  const pick = (value) => {
    if (q.type === "single") {
      setAnswers((a) => ({ ...a, [q.id]: value }));
      // Auto-advance smoothly to the next question
      clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => {
        if (step < QUESTIONS.length - 1) setStep((s) => s + 1);
      }, 280);
    } else {
      const cur = answers[q.id] || [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : cur.length < 3 ? [...cur, value] : cur;
      setAnswers((a) => ({ ...a, [q.id]: next }));
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        symptoms: [answers.when].filter(Boolean),
        pain_areas: [answers.area].filter(Boolean),
        goals: answers.goal || [],
        additional_notes: [
          answers.duration ? `Duration: ${answers.duration}` : null,
          answers.routine ? `Routine: ${answers.routine}` : null,
        ].filter(Boolean).join(". "),
      };
      const { data } = await api.post(`/ai/symptom-check`, payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setStep(0); setAnswers({}); setResult(null); setGoal(null); };
  const onLast = step === QUESTIONS.length - 1;
  const multiReady = q?.type === "multi" && Array.isArray(current) && current.length > 0;

  // ---------- RESULT ----------
  if (result) {
    return (
      <div data-testid="symptom-checker-page" className="min-h-screen welljio-mesh pt-28 pb-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 animate-fade-up" data-testid="quiz-result">
          <span className="welljio-eyebrow">Your personalised match</span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-[5.5rem] font-light tracking-[-0.04em] leading-[0.92] mt-6 text-welljio-ink">
            Here's what we'd<br /><span className="italic font-normal welljio-underline">recommend.</span>
          </h1>
          <p className="text-welljio-muted leading-relaxed text-base sm:text-lg mt-6 max-w-2xl whitespace-pre-line" data-testid="ai-explanation">
            {result.explanation}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {result.recommendations.map((p, i) => (<ProductCard product={p} key={p.id} index={i} />))}
          </div>
          <div className="mt-12 pt-6 border-t border-welljio-ink/15 flex flex-wrap items-center justify-between gap-4">
            <button data-testid="quiz-restart-button" onClick={reset} className="inline-flex items-center gap-1.5 text-sm text-welljio-muted hover:text-welljio-accent">
              <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.6} /> Retake the quiz
            </button>
            <Link to="/shop" data-testid="quiz-shop-all-link" className="text-sm font-medium text-welljio-accent underline underline-offset-[6px] decoration-welljio-accent/30 hover:decoration-welljio-coral hover:text-welljio-coral">
              Or browse the full collection →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---------- IMMERSIVE STAGE (under fixed navbar) ----------
  return (
    <div data-testid="symptom-checker-page" className="relative welljio-mesh min-h-[calc(100vh-5rem)] pt-20 overflow-hidden">
      {/* Top hairline editorial bar — mirrors homepage hero */}
      <div className="border-b border-welljio-ink/10 relative z-10 bg-white/40 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-11 flex items-center justify-between text-[10px] uppercase tracking-[0.36em] text-welljio-accent font-semibold">
          <span data-testid="hero-masthead">Welljio · Vol. I</span>
          <span className="hidden md:flex items-center gap-4 text-welljio-ink/55 font-medium">
            <span>Find My Fit</span>
            <span className="text-welljio-coral">/</span>
            <span>Physiotherapy-led</span>
            <span className="text-welljio-coral">/</span>
            <span>2 minutes</span>
          </span>
          <span className="tabular-nums text-welljio-ink/55 font-medium">
            {goal ? `N° ${String(step + 1).padStart(2, "0")} — 05` : "N° 00 — 05"}
          </span>
        </div>
      </div>

      {/* Subtle backdrop accents — cobalt × coral */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 -left-40 w-[480px] h-[480px] rounded-full bg-welljio-highlight/50 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full bg-welljio-coralSoft/40 blur-3xl" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-12 sm:py-16">
        {!goal ? (
          <GoalPicker onSelect={selectGoal} />
        ) : (
          <div className="max-w-4xl mx-auto">
            <QuizStage
              q={q}
              step={step}
              total={QUESTIONS.length}
              current={current}
              pick={pick}
              goalLabel={goal.label}
              onBack={() => (step === 0 ? reset() : setStep(step - 1))}
              onSubmit={submit}
              loading={loading}
              multiReady={multiReady}
              onLast={onLast}
            />
          </div>
        )}
      </div>

      {/* Bottom hairline marquee — mirrors homepage hero */}
      {!goal && (
        <div className="border-t border-welljio-ink/10 relative z-10 bg-white/40 backdrop-blur-sm">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-6 text-[11px] uppercase tracking-[0.28em] text-welljio-muted font-medium overflow-x-auto whitespace-nowrap">
            <span className="flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-welljio-coral shrink-0" strokeWidth={1.6} />
              <span className="text-welljio-ink font-semibold">Personalised</span>
              <span className="text-welljio-coral">·</span>
              by physiotherapists
            </span>
            <span className="hidden md:inline text-welljio-coral">/</span>
            <span className="hidden md:inline">5 questions</span>
            <span className="hidden md:inline text-welljio-coral">/</span>
            <span className="hidden lg:inline">2-minute quiz</span>
            <span className="hidden lg:inline text-welljio-coral">/</span>
            <span className="hidden md:inline">Not a diagnosis</span>
            <span className="hidden md:inline text-welljio-coral">/</span>
            <span className="text-welljio-accent font-semibold">Free to take</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* -------- Sub-components -------- */

function GoalPicker({ onSelect }) {
  return (
    <div className="animate-fade-up text-center" data-testid="goal-picker">
      <span className="welljio-pill mb-8 mx-auto" data-testid="find-my-fit-pill">
        <span className="relative flex w-1.5 h-1.5">
          <span className="animate-pulse-soft absolute inline-flex w-full h-full rounded-full bg-welljio-coral opacity-70"></span>
          <span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-welljio-coral"></span>
        </span>
        Smart match · Physiotherapy-led
      </span>
      <h1 className="font-heading font-light text-welljio-ink tracking-[-0.045em] leading-[0.9] text-[clamp(2.75rem,7vw,6.5rem)]">
        Where shall <br className="sm:hidden" />we
        <br />
        <span className="welljio-underline italic font-normal text-welljio-accent">start?</span>
      </h1>
      <p className="text-welljio-muted text-base sm:text-lg mt-7 max-w-xl mx-auto leading-relaxed">
        Pick the goal closest to what's bothering you — we'll launch a short, personalised quiz tuned to it.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12 max-w-4xl mx-auto">
        {GOALS.map((g) => {
          const Icon = g.icon;
          return (
            <button
              key={g.id}
              data-testid={`goal-${g.id}`}
              onClick={() => onSelect(g)}
              className="group welljio-premium-card p-5 text-left"
            >
              <span className="w-10 h-10 rounded-xl bg-welljio-highlight text-welljio-accent flex items-center justify-center mb-3 group-hover:bg-welljio-coralSoft group-hover:text-welljio-coral transition-colors">
                <Icon className="w-4 h-4" strokeWidth={1.6} />
              </span>
              <p className="font-heading text-base font-semibold text-welljio-ink leading-tight">{g.label}</p>
              <p className="text-xs text-welljio-muted mt-1 leading-snug">{g.blurb}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuizStage({ q, step, total, current, pick, goalLabel, onBack, onSubmit, loading, multiReady, onLast }) {
  return (
    <div key={q.id} className="animate-fade-up" data-testid={`quiz-step-${q.id}`}>
      {/* Top: back link + progress dots */}
      <div className="flex items-center justify-between mb-7">
        <button
          data-testid="quiz-back-button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-xs text-welljio-ink/65 hover:text-welljio-ink"
        >
          <ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.6} />
          {step === 0 ? "Change goal" : "Back"}
        </button>
        <div className="flex items-center gap-1.5" data-testid="quiz-progress">
          {[...Array(total)].map((_, i) => (
            <span key={i} className={`h-1 rounded-full transition-all ${i === step ? "w-7 bg-welljio-accent" : i < step ? "w-2.5 bg-welljio-accent/60" : "w-2.5 bg-welljio-ink/15"}`} />
          ))}
        </div>
        <span className="text-[10px] uppercase tracking-[0.32em] text-welljio-ink/55 tabular-nums">
          <span className="text-welljio-ink/70 font-medium">{goalLabel}</span>
          <span className="mx-2 text-welljio-ink/25">·</span>
          {String(step + 1).padStart(2, "0")}/{String(total).padStart(2, "0")}
        </span>
      </div>

      <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-[-0.03em] leading-[1.04] text-welljio-ink text-center max-w-3xl mx-auto">
        {q.title}
      </h2>
      {q.subtitle && (
        <p className="text-center text-welljio-muted text-sm mt-3 max-w-xl mx-auto">{q.subtitle}</p>
      )}

      <div className={`mt-7 grid gap-2.5 max-w-2xl mx-auto ${q.type === "multi" ? "sm:grid-cols-2" : ""}`}>
        {q.options.map((opt) => {
          const selected = q.type === "single" ? current === opt.value : Array.isArray(current) && current.includes(opt.value);
          return (
            <button
              key={opt.value}
              data-testid={`option-${q.id}-${opt.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => pick(opt.value)}
              className={`group w-full text-left flex items-start gap-3 px-4 py-3 rounded-xl border transition-all ${
                selected
                  ? "border-welljio-accent bg-welljio-accent text-white shadow-[0_10px_30px_-15px_rgba(0,64,128,0.55)]"
                  : "border-welljio-ink/10 bg-white/80 text-welljio-ink hover:border-welljio-accent/50 hover:bg-white"
              }`}
            >
              <span className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 transition-colors ${selected ? "border-white bg-white" : "border-welljio-ink/30 group-hover:border-welljio-accent"}`}>
                {selected && <span className="block w-1.5 h-1.5 rounded-full bg-welljio-accent m-[3px]" />}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm sm:text-base font-medium">{opt.label}</span>
                {opt.hint && (
                  <span className={`block text-xs mt-0.5 ${selected ? "text-white/65" : "text-welljio-ink/55"}`}>{opt.hint}</span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* Show submit only for the multi-select final step */}
      {onLast && q.type === "multi" && (
        <div className="mt-7 flex justify-center">
          <button
            data-testid="quiz-submit-button"
            onClick={onSubmit}
            disabled={!multiReady || loading}
            className="inline-flex items-center gap-2 bg-welljio-accent text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-welljio-coral disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Finding your match…</>) : (<>Show my match <Sparkles className="w-4 h-4" strokeWidth={1.6} /></>)}
          </button>
        </div>
      )}
    </div>
  );
}
